import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '@siaedts/shared/lib/supabase';

export function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [news, setNews] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadNews() {
      if (!slug) return;
      
      try {
        setLoading(true);
        const data = await db.news.getBySlug(slug);
        setNews(data);
        setError(null);
      } catch (err) {
        setError('Failed to load news article');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    loadNews();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error loading news</h2>
          <p className="text-gray-600">{error}</p>
          <Link to="/news" className="mt-4 inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-600 mb-4">News article not found</h2>
          <Link to="/news" className="mt-4 inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-8">
          <div className="mb-6">
            <time className="text-sm text-gray-500">
              {new Date(news.published_at || news.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </div>
          
          <h1 className="mb-6 text-3xl font-bold text-gray-900">{news.title}</h1>
          
          {news.excerpt && (
            <p className="mb-6 text-lg text-gray-600">{news.excerpt}</p>
          )}
          
          {news.featured_image_url && (
            <div className="mb-8 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-2">
              <div className="relative w-full overflow-hidden rounded-lg bg-gray-50" style={{ maxHeight: '72vh' }}>
                <img 
                  src={news.featured_image_url} 
                  alt={news.title} 
                  className="block h-auto max-h-[72vh] w-full rounded-lg object-contain mx-auto"
                />
              </div>
            </div>
          )}

          {Array.isArray(news.gallery_images) && news.gallery_images.length > 0 && (
            <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {news.gallery_images.map((image: string, index: number) => (
                <img
                  key={`${image}-${index}`}
                  src={image}
                  alt={`${news.title} - ${index + 1}`}
                  className="h-auto max-h-[480px] w-full rounded-lg object-cover"
                  loading="lazy"
                />
              ))}
            </div>
          )}
          
          <div className="prose prose-lg max-w-none text-gray-800">
            {!!news.content && (
              <div dangerouslySetInnerHTML={{ __html: news.content }} />
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-8">
        <Link 
          to="/news" 
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          ← Back to News
        </Link>
      </div>
    </div>
  );
}